/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { getApiBaseUrl } from '../../../../../core/config/api';

export type TabKey = 'buildings' | 'vehicles' | 'staff' | 'interventions';

export type PossessionData = {
  buildings: any[];
  vehicles: any[];
  staff: any[];
  interventions: any[];
};

export type PlayerProfile = {
  id: number;
  username: string;
  email: string;
  roles_id: number;
  activated: number;
  argents: number;
  medailles: number;
  cta_prise_appels?: number;
  premium?: number;
  xp?: number;
  supports_premium?: number;
  supports_xp?: number;
};

export type LookupRefs = {
  vehicle_statuses: Array<{ id: number; code: string; nom: string; disponible?: number }>;
  staff_statuses: Array<{ id: number; code: string; nom: string; disponible?: number }>;
  config_personnel: Array<{ id: number; grade: string; grade_code: string; ordre?: number }>;
  config_vehicles: Array<{ id: number; code: string; nom: string; specialiste?: number }>;
  planning_statuses: string[];
};

interface UseModerationStateOpts {
  userId: number;
}

/** Hook extrait de useModerationManage : état + load functions. */
export function useModerationManageState({ userId }: UseModerationStateOpts) {
  const [tab, setTab] = React.useState<TabKey>('buildings');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [possessionData, setPossessionData] = React.useState<PossessionData | null>(null);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editForm, setEditForm] = React.useState<Record<string, string>>({});
  const [searchByTab, setSearchByTab] = React.useState<Record<TabKey, string>>({
    buildings: '',
    vehicles: '',
    staff: '',
    interventions: '',
  });
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [economyAdjusting, setEconomyAdjusting] = React.useState(false);
  const [economyAction, setEconomyAction] = React.useState<{
    argents: { amount: string; reason: string };
    medailles: { amount: string; reason: string };
  }>({
    argents: { amount: '', reason: '' },
    medailles: { amount: '', reason: '' },
  });
  const [playerProfile, setPlayerProfile] = React.useState<{
    id: number;
    username: string;
    email: string;
    roles_id: number;
    activated: number;
    argents: number;
    medailles: number;
    cta_prise_appels?: number;
    premium?: number;
    xp?: number;
    supports_premium?: number;
    supports_xp?: number;
  } | null>(null);
  const [ctaScenarios, setCtaScenarios] = React.useState<Array<{ id: number; scenario_name?: string }>>([]);
  const [ctaBuildings, setCtaBuildings] = React.useState<Array<{ id: number; nom: string; actif: number }>>([]);
  const [buildingCtaLink, setBuildingCtaLink] = React.useState<Record<number, string>>({});
  const [generatedScenarioId, setGeneratedScenarioId] = React.useState<string>('');
  const [lookup, setLookup] = React.useState<{
    vehicle_statuses: Array<{ id: number; code: string; nom: string; disponible?: number }>;
    staff_statuses: Array<{ id: number; code: string; nom: string; disponible?: number }>;
    config_personnel: Array<{ id: number; grade: string; grade_code: string; ordre?: number }>;
    config_vehicles: Array<{ id: number; code: string; nom: string; specialiste?: number }>;
    planning_statuses: string[];
  }>({ vehicle_statuses: [], staff_statuses: [], config_personnel: [], config_vehicles: [], planning_statuses: [] });

  const items = React.useMemo(() => (possessionData ? possessionData[tab] : []) || [], [possessionData, tab]);
  const buildingOptions = React.useMemo(() => possessionData?.buildings || [], [possessionData]);
  const searchTerm = React.useMemo(() => String(searchByTab[tab] || '').trim().toLowerCase(), [searchByTab, tab]);
  const filteredItems = React.useMemo(() => {
    if (!searchTerm) return items;
    return items.filter((item: any) => {
      const haystack = [
        item?.id,
        item?.nom,
        item?.prenom,
        item?.numero_intervention,
        item?.adresse,
        item?.building_type_name,
        item?.vehicle_type_name,
        item?.vehicle_code,
        item?.personnel_grade,
        item?.personnel_grade_code,
        item?.staff_status_nom,
        item?.vehicle_status_nom,
        item?.planning_statut_nom,
        item?.statut,
        item?.batiment_nom,
      ]
        .map((x) => String(x ?? ''))
        .join(' ')
        .toLowerCase();
      return haystack.includes(searchTerm);
    });
  }, [items, searchTerm]);
  const vehicleStatusCodeById = React.useMemo(() => {
    const map = new Map<number, string>();
    lookup.vehicle_statuses.forEach((s) => map.set(Number(s.id), String(s.code || '').toLowerCase()));
    return map;
  }, [lookup.vehicle_statuses]);
  const vehicleStatusCodeByName = React.useMemo(() => {
    const map = new Map<string, string>();
    lookup.vehicle_statuses.forEach((s) => {
      const nom = String(s.nom || '').trim().toLowerCase();
      if (nom) map.set(nom, String(s.code || '').toLowerCase());
    });
    return map;
  }, [lookup.vehicle_statuses]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement possessions');
      const p = data.data as PossessionData;
      setPossessionData({
        buildings: Array.isArray(p?.buildings) ? p.buildings : [],
        vehicles: Array.isArray(p?.vehicles) ? p.vehicles : [],
        staff: Array.isArray(p?.staff) ? p.staff : [],
        interventions: Array.isArray(p?.interventions) ? p.interventions : [],
      });
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadProfile = React.useCallback(async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/player/${userId}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement profil');
      setPlayerProfile(data.data || null);
    } catch (e: any) {
      setError(e?.message || 'Erreur chargement profil');
    }
  }, [userId]);

  const loadLookupRefs = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/lookup-refs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) return;
      setLookup(
        data.data || {
          vehicle_statuses: [],
          staff_statuses: [],
          config_personnel: [],
          config_vehicles: [],
          planning_statuses: [],
        },
      );
    } catch {
      // ignore
    }
  }, []);

  const loadCtaScenarios = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/cta/scenarios?page=1&limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) return;
      setCtaScenarios(Array.isArray(data.data) ? data.data : []);
    } catch {
      // ignore
    }
  }, []);

  const loadCtaBuildings = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/cta-buildings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) return;
      setCtaBuildings(Array.isArray(data.data) ? data.data : []);
    } catch {
      // ignore
    }
  }, [userId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  React.useEffect(() => {
    void loadLookupRefs();
  }, [loadLookupRefs]);

  React.useEffect(() => {
    if (tab === 'interventions') void loadCtaScenarios();
  }, [tab, loadCtaScenarios]);

  React.useEffect(() => {
    if (tab === 'buildings') void loadCtaBuildings();
  }, [tab, loadCtaBuildings]);



  return {
    tab,
    setTab,
    loading,
    setLoading,
    error,
    setError,
    possessionData,
    setPossessionData,
    editingId,
    setEditingId,
    editForm,
    setEditForm,
    searchByTab,
    setSearchByTab,
    savingProfile,
    setSavingProfile,
    economyAdjusting,
    setEconomyAdjusting,
    economyAction,
    setEconomyAction,
    playerProfile,
    setPlayerProfile,
    ctaScenarios,
    setCtaScenarios,
    ctaBuildings,
    setCtaBuildings,
    buildingCtaLink,
    setBuildingCtaLink,
    generatedScenarioId,
    setGeneratedScenarioId,
    lookup,
    setLookup,
    items,
    buildingOptions,
    searchTerm,
    filteredItems,
    vehicleStatusCodeById,
    vehicleStatusCodeByName,
    load,
    loadProfile,
    loadLookupRefs,
    loadCtaScenarios,
    loadCtaBuildings,
  };
}
